import { useEffect, useMemo, useRef, useState } from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';
import { Mic, Paperclip, X } from 'lucide-react';
import { AvatarCircle, Button, Input, cn } from '@jeraisonne/ui';
import { DOCUMENT_ACCEPT, DocumentError, createDocumentsClient } from '@jeraisonne/documents';
import { ChatFileCard } from './chat-file-card';
import {
  SupabaseMessagingService,
  type VisioMessage,
  type VisioParticipantInfo,
} from '../chat/messaging-service';
import type { VisioRole } from '../video/video-service';

/** Remplace un message existant (même `id` : masqué/rétabli) ou l'ajoute (nouveau). */
export function upsertMessage(list: VisioMessage[], incoming: VisioMessage): VisioMessage[] {
  const index = list.findIndex((m) => m.id === incoming.id);
  if (index === -1) return [...list, incoming];
  const next = [...list];
  next[index] = incoming;
  return next;
}

/**
 * Chat de la salle. Deux dispositions, une seule arborescence (donc un seul
 * champ de saisie, une seule source de vérité pour le brouillon) :
 * - **mobile** (< lg) : la liste défile dans la page ; la barre de saisie est
 *   FIXÉE en bas de l'écran, juste au-dessus de la barre d'actions
 *   (`--visio-bar-h`, posée par `RoomPage` — 0 si le panneau est utilisé seul).
 * - **desktop** (lg+) : colonne de droite pleine hauteur, saisie en bas à droite.
 *
 * Chaque message porte l'AVATAR de son expéditeur (RPC `get_visio_room_participants`
 * : la RLS de `profiles` ne garantit pas de pouvoir lire l'autre participant) et
 * une petite croix qui le MASQUE (suppression douce, réversible — migration
 * 0246) : l'auteur masque ses messages, le tuteur n'importe lesquels. Un message
 * masqué disparaît du chat des DEUX participants et n'entre pas dans le résumé.
 *
 * Fichiers (migrations 0261-0262) : un compte réel joint un fichier (trombone) ;
 * il est déposé dans la salle (`documents`, portée `room`) puis publié dans le
 * chat (`send_visio_room_file`). Un INVITÉ anonyme (`myRole==='guest'`) voit et
 * ouvre les cartes mais n'a pas de trombone : la RLS lui refuse l'écriture.
 */
export function ChatPanel({
  roomId,
  myUserId,
  myRole,
  db,
}: {
  roomId: string;
  myUserId: string;
  myRole: VisioRole;
  db: SupabaseClient;
}) {
  const messagingService = useMemo(() => new SupabaseMessagingService(db), [db]);
  const documents = useMemo(() => createDocumentsClient(db), [db]);
  const canShareFiles = myRole !== 'guest';
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadingName, setUploadingName] = useState<string | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  /** Fichier déjà enregistré dans la salle mais dont la publication dans le chat a échoué. */
  const [unpublishedDocId, setUnpublishedDocId] = useState<string | null>(null);
  const [messages, setMessages] = useState<VisioMessage[]>([]);
  const [people, setPeople] = useState<Record<string, VisioParticipantInfo>>({});
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let cancelled = false;
    void messagingService.history(roomId).then((history) => {
      if (!cancelled) setMessages(history);
    });
    void messagingService
      .participants(roomId)
      .then((list) => {
        if (!cancelled) setPeople(Object.fromEntries(list.map((p) => [p.userId, p])));
      })
      .catch((err: unknown) => {
        // Sans avatars le chat reste utilisable (pastilles neutres).
        console.error('[chat] participants indisponibles', err);
      });
    const off = messagingService.onMessage(roomId, (message) => {
      setMessages((prev) => upsertMessage(prev, message));
    });
    return () => {
      cancelled = true;
      off();
    };
  }, [roomId, messagingService]);

  // Les messages masqués ne s'affichent jamais dans le chat.
  const visible = useMemo(() => messages.filter((m) => !m.hiddenAt), [messages]);

  useEffect(() => {
    const list = listRef.current;
    if (list) list.scrollTop = list.scrollHeight;
  }, [visible]);

  async function handleHide(message: VisioMessage) {
    // Optimiste : le message disparaît tout de suite ; rétabli si le serveur refuse.
    setMessages((prev) => upsertMessage(prev, { ...message, hiddenAt: new Date().toISOString() }));
    try {
      const updated = await messagingService.setHidden(message.id, true);
      setMessages((prev) => upsertMessage(prev, updated));
    } catch (err) {
      console.error('[chat] hide failed', err);
      setMessages((prev) => upsertMessage(prev, message));
    }
  }

  async function handleSend() {
    const body = draft.trim();
    if (!body || sending) return;
    setSending(true);
    setDraft('');
    try {
      await messagingService.send(roomId, body);
    } catch (err) {
      console.error('[chat] send failed', err);
    } finally {
      setSending(false);
    }
  }

  async function publishFile(documentId: string) {
    try {
      await messagingService.sendFile(roomId, documentId);
      setUnpublishedDocId(null);
      setFileError(null);
    } catch (err) {
      console.error('[chat] publication du fichier échouée', err);
      setUnpublishedDocId(documentId);
      setFileError(
        "Le fichier est enregistré dans les fichiers de la réunion, mais n'a pas pu être publié dans le chat.",
      );
    }
  }

  async function handleFilePicked(file: File | undefined) {
    if (!file || uploadingName) return;
    setFileError(null);
    setUnpublishedDocId(null);
    setUploadingName(file.name);
    try {
      const doc = await documents.upload({
        file,
        target: { kind: 'room', roomId },
        userId: myUserId,
      });
      await publishFile(doc.id);
    } catch (err) {
      console.error('[chat] envoi du fichier échoué', err);
      setFileError(
        err instanceof DocumentError ? err.message : "Le fichier n'a pas pu être envoyé. Réessaie.",
      );
    } finally {
      setUploadingName(null);
    }
  }

  return (
    <div className="border-ink-100 flex flex-col rounded-2xl border bg-white lg:h-full">
      <div
        ref={listRef}
        className="max-h-[40dvh] min-h-[8rem] flex-1 space-y-2 overflow-y-auto p-3 lg:max-h-none lg:min-h-0"
      >
        {visible.length === 0 && (
          <p className="text-ink-400 py-6 text-center text-sm">Aucun message pour l'instant.</p>
        )}
        {visible.map((m) => {
          const mine = m.senderId === myUserId;
          const sender = people[m.senderId];
          // L'auteur masque ses messages ; le tuteur peut masquer ceux de l'autre aussi.
          const canHide = mine || myRole === 'tutor';
          return (
            <div key={m.id} className={cn('flex items-end gap-2', mine && 'flex-row-reverse')}>
              <AvatarCircle name={sender?.displayName || '?'} src={sender?.avatarUrl} size="sm" />
              <div
                className={cn(
                  'flex max-w-[75%] items-start gap-1.5 rounded-2xl px-3 py-2 text-sm',
                  mine ? 'bg-primary-500 text-white' : 'bg-ink-50 text-ink-900',
                )}
              >
                {m.source === 'voice' && (
                  <Mic
                    className={cn(
                      'mt-0.5 h-3.5 w-3.5 shrink-0',
                      mine ? 'text-white/70' : 'text-ink-400',
                    )}
                    aria-label="Retranscrit automatiquement"
                  />
                )}
                {m.source === 'file' ? (
                  <ChatFileCard
                    documentId={m.documentId}
                    fallbackName={m.body}
                    mine={mine}
                    documents={documents}
                  />
                ) : (
                  <span className="break-words">{m.body}</span>
                )}
              </div>
              {canHide && (
                // Petite croix visuelle, mais zone de tap de 44 px (`-my-2` : elle ne grossit pas la ligne).
                <button
                  type="button"
                  onClick={() => void handleHide(m)}
                  aria-label="Masquer ce message"
                  title="Masquer ce message"
                  className="text-ink-300 hover:text-ink-600 -my-2 flex size-11 shrink-0 items-center justify-center self-center transition-colors active:scale-90"
                >
                  <X className="size-3.5" />
                </button>
              )}
            </div>
          );
        })}
        {uploadingName && (
          <p role="status" className="text-ink-500 text-center text-xs">
            Envoi de « {uploadingName} »…
          </p>
        )}
        {fileError && (
          <p role="alert" className="text-accent-700 text-center text-xs font-medium">
            {fileError}{' '}
            {unpublishedDocId && (
              <button
                type="button"
                onClick={() => void publishFile(unpublishedDocId)}
                className="min-h-[2.75rem] underline underline-offset-2"
              >
                Réessayer
              </button>
            )}
          </p>
        )}
      </div>
      <form
        className="border-ink-100 fixed inset-x-0 bottom-[var(--visio-bar-h,0px)] z-10 flex gap-2 border-t bg-white/95 p-2 backdrop-blur lg:static lg:inset-auto lg:z-auto lg:bg-transparent lg:backdrop-blur-none"
        onSubmit={(e) => {
          e.preventDefault();
          void handleSend();
        }}
      >
        {canShareFiles && (
          <>
            <input
              ref={fileInputRef}
              type="file"
              accept={DOCUMENT_ACCEPT}
              className="hidden"
              aria-label="Choisir un fichier à partager"
              onChange={(e) => {
                const file = e.target.files?.[0];
                e.target.value = ''; // permet de re-choisir le même fichier
                void handleFilePicked(file);
              }}
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploadingName !== null}
              aria-label="Joindre un fichier"
              title="Joindre un fichier"
              className="text-ink-500 hover:text-ink-800 flex min-h-[2.75rem] min-w-[2.75rem] shrink-0 items-center justify-center rounded-xl transition-colors active:scale-95 disabled:opacity-50"
            >
              <Paperclip className="size-5" />
            </button>
          </>
        )}
        <Input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="Message..."
          className="min-h-[2.75rem] flex-1"
        />
        <Button type="submit" disabled={!draft.trim() || sending} className="min-h-[2.75rem]">
          Envoyer
        </Button>
      </form>
    </div>
  );
}
